/* GitHub-style activity heatmap — last 90 days × per-day question count.

   Renders an HTML/CSS-grid heatmap instead of an SVG so the cells
   stretch horizontally to fill the host card's width while row height
   stays pinned. The viewport-fixed-height + viewport-fluid-width
   shape gives the desired "wide, short" activity card look.

   Layout: 7 rows (Mon..Sun) × ~13 columns (one per week, current week
   LEFT, oldest week RIGHT — reverse chronological so the eye lands on
   "where am I right now" first). Color intensity scales with the
   day's question count.

   Public API:
     renderHeatmap(progress) → HTML string ready to drop into innerHTML
*/

const CAL_DAYS = 90;
const ROWS = 7;
const ROW_H = 11;        // px — fixed cell height (drives card height)
const CELL_GAP = 2;      // px — gap between cells

// Skip a month label if it would render within this many columns of
// the previous one (prevents "MarFeb" collisions when month boundaries
// land on adjacent columns).
const MIN_LABEL_COL_GAP = 2;

function dayKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function bucketColor(count) {
  // 7-step gradient — finer at the low end so a casual day stands
  // out from an empty day, finer at the high end so a heavy review
  // session looks visibly different from a normal one. Linear ramps
  // would compress the most-common 1-20 range into one shade; these
  // breakpoints are log-ish so each bucket holds roughly the same
  // number of real days.
  if (count === 0)   return "var(--heatmap-0, rgba(127,127,127,0.10))";
  if (count <= 3)    return "var(--heatmap-1, rgba(45,212,191,0.22))";
  if (count <= 9)    return "var(--heatmap-2, rgba(45,212,191,0.40))";
  if (count <= 25)   return "var(--heatmap-3, rgba(45,212,191,0.58))";
  if (count <= 60)   return "var(--heatmap-4, rgba(45,212,191,0.74))";
  if (count <= 150)  return "var(--heatmap-5, rgba(45,212,191,0.88))";
  return                    "var(--heatmap-6, rgba(45,212,191,1.00))";
}

/**
 * @param {object} progress - { [qid]: { lastSeen, … }, … } from allProgress()
 * @returns {string} HTML
 */
export function renderHeatmap(progress) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Range start: CAL_DAYS-1 days before today (inclusive window).
  const start = new Date(today);
  start.setDate(start.getDate() - (CAL_DAYS - 1));

  // Build dayKey → count from the progress records' lastSeen / updated
  // timestamps, scoped to the window.
  const counts = new Map();
  const list = Array.isArray(progress) ? progress : [];
  for (const rec of list) {
    const ts = rec && (rec.updated || rec.lastSeen || rec.last_attempt);
    if (!ts) continue;
    const d = new Date(ts);
    if (isNaN(d.getTime())) continue;
    d.setHours(0, 0, 0, 0);
    if (d < start || d > today) continue;
    const k = dayKey(d);
    counts.set(k, (counts.get(k) || 0) + 1);
  }

  // Find the Monday on or before `start` so we have aligned week
  // columns. (week-of-Mon=0, Sun=6.)
  const weekStart = new Date(start);
  const dow = (weekStart.getDay() + 6) % 7;
  weekStart.setDate(weekStart.getDate() - dow);

  const totalCols = Math.ceil((today - weekStart) / (7 * 24 * 3600 * 1000)) + 1;
  const totalDays = totalCols * 7;

  const cellHtml = [];
  const monthMarkers = []; // { colVisual, name }  colVisual: 0 = leftmost
  let totalAnswered = 0;
  let activeDays = 0;
  let currentMonth = -1;

  for (let i = 0; i < totalDays; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    const col = Math.floor(i / 7);                // 0 = oldest week
    const row = i % 7;                            // 0 = Monday
    // Current week sits on the LEFT, oldest week on the RIGHT.
    const colVisual = (totalCols - 1) - col;

    // Position in the CSS grid is 1-indexed.
    const styleAttr = `grid-column: ${colVisual + 1}; grid-row: ${row + 1};`;

    if (d > today || d < start) {
      // Out-of-window padding cell — empty/transparent so the grid
      // shape stays rectangular without showing data.
      cellHtml.push(`<span class="hm-cell hm-cell-pad" style="${styleAttr}"></span>`);
      continue;
    }

    const k = dayKey(d);
    const count = counts.get(k) || 0;
    if (count > 0) { totalAnswered += count; activeDays += 1; }
    const fill = bucketColor(count);
    const tooltip = count === 0
      ? `${formatDay(d)}, no activity`
      : `${formatDay(d)}, ${count} question${count === 1 ? "" : "s"}`;
    cellHtml.push(
      `<span class="hm-cell" style="${styleAttr} background: ${fill};" ` +
      `title="${escAttr(tooltip)}"></span>`,
    );

    // Note month boundaries on the top row so we can render a label
    // strip above the grid.
    if (row === 0) {
      const m = d.getMonth();
      if (m !== currentMonth) {
        currentMonth = m;
        monthMarkers.push({ colVisual, name: MONTH_NAMES[m] });
      }
    }
  }

  // Filter month markers by MIN_LABEL_COL_GAP so we don't get the
  // "MarFeb" collision. Sort by colVisual ascending (left to right)
  // and keep the leftmost of any cluster.
  monthMarkers.sort((a, b) => a.colVisual - b.colVisual);
  const monthMarkersKept = [];
  let lastCol = -Infinity;
  for (const m of monthMarkers) {
    if (m.colVisual - lastCol >= MIN_LABEL_COL_GAP) {
      monthMarkersKept.push(m);
      lastCol = m.colVisual;
    }
  }

  // Position month labels above their starting column. Centered via
  // translateX(-50%) so the label hovers nicely over the column edge.
  const monthHtml = monthMarkersKept
    .map((m) => {
      const pct = ((m.colVisual + 0.5) / totalCols) * 100;
      return `<span class="hm-month-label" style="left: ${pct.toFixed(2)}%;">${escAttr(m.name)}</span>`;
    })
    .join("");

  return `
    <div class="hm-summary muted">
      <strong>${totalAnswered}</strong> question${totalAnswered === 1 ? "" : "s"}
      across <strong>${activeDays}</strong> active day${activeDays === 1 ? "" : "s"}
      in the last ${CAL_DAYS} days.
    </div>
    <div class="hm-grid-wrap">
      <div class="hm-rowlabels" aria-hidden="true">
        <span>M</span>
        <span></span>
        <span>W</span>
        <span></span>
        <span>F</span>
        <span></span>
        <span>S</span>
      </div>
      <div class="hm-grid-main">
        <div class="hm-month-labels" aria-hidden="true">${monthHtml}</div>
        <div class="hm-cells" role="img"
             aria-label="Daily activity over the last ${CAL_DAYS} days"
             style="--hm-cols: ${totalCols};">
          ${cellHtml.join("")}
        </div>
      </div>
    </div>
    <div class="hm-legend muted">
      <span>Less</span>
      <span class="hm-swatch" style="background: ${bucketColor(0)}"></span>
      <span class="hm-swatch" style="background: ${bucketColor(2)}"></span>
      <span class="hm-swatch" style="background: ${bucketColor(7)}"></span>
      <span class="hm-swatch" style="background: ${bucketColor(18)}"></span>
      <span class="hm-swatch" style="background: ${bucketColor(45)}"></span>
      <span class="hm-swatch" style="background: ${bucketColor(100)}"></span>
      <span class="hm-swatch" style="background: ${bucketColor(200)}"></span>
      <span>More</span>
    </div>`;
}

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatDay(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function escAttr(s) {
  return String(s ?? "").replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;").replaceAll('"',"&quot;");
}
